import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where
} from 'firebase/firestore';
import { db, appId } from '../../../firebase-config';
import { getActiveCompanyId } from '../../../core/company/companyConfig';
import { resolveContactsCollectionPath } from '../../../core/company/companyPaths';
import {
  ARGENTINA_PROVINCES,
  DESTINATION_OPERATION_ROLES,
  Icon,
  OPERATIONAL_LOAD_OPERATION_TYPES,
  OPERATIONAL_LOAD_UNITS,
  geocodeAddress,
  getDestinationOperationRoleLabel,
  normalizeDestinationOperationalFields,
  normalizeOperationalLoad
} from './LogisticsUtils';

const DEFAULT_COMPANY_ID = 'default';
const buildDestinationValidatedAddress = (values = {}) =>
  [
    values.address,
    values.apartment,
    values.locality,
    values.partido,
    values.province,
    values.postalCode,
    'Argentina'
  ]
    .filter(Boolean)
    .join(', ');

const createDestinationGeoState = ({
  status = 'idle',
  text = 'Completá la dirección y validala para dejar el destino listo para operar.',
  validatedAddress = ''
} = {}) => ({
  status,
  text,
  validatedAddress
});

const createEmptyOperationalLoad = (destinationId = '') =>
  normalizeOperationalLoad({
    title: '',
    description: '',
    quantity: 1,
    unit: 'unidad',
    operationType: 'pickup',
    status: 'planned',
    category: '',
    referenceCode: '',
    originStopId: destinationId || '',
    targetStopId: '',
    currentStopId: null,
    requiresEvidenceOnPickup: false,
    requiresEvidenceOnDelivery: false,
    pickupEvidence: [],
    deliveryEvidence: [],
    notes: ''
  });

const DestinationFormModal = ({
  route,
  destination,
  onClose,
  showMessage,
  contacts,
  onSave,
  companyId
}) => {
  const isEditing = !!destination;

  const resolvedCompanyId =
    companyId ||
    route?.companyId ||
    getActiveCompanyId() ||
    DEFAULT_COMPANY_ID;

  const contactsCollectionPath = useMemo(() => {
    try {
      return resolveContactsCollectionPath(resolvedCompanyId);
    } catch (errorByString) {
      try {
        return resolveContactsCollectionPath({ appId, companyId: resolvedCompanyId });
      } catch (errorByObject) {
        console.warn(
          'No se pudo resolver path de contactos por empresa. Se usa fallback legacy.',
          { errorByString, errorByObject, resolvedCompanyId }
        );
        return `artifacts/${appId}/public/data/contacts`;
      }
    }
  }, [resolvedCompanyId]);

  const [formData, setFormData] = useState(
    normalizeDestinationOperationalFields(
      isEditing
        ? {
            ...destination,
            apartment: destination.apartment || '',
            locality: destination.locality || '',
            postalCode: destination.postalCode || ''
          }
        : {
            id: Date.now().toString(),
            name: '',
            address: '',
            apartment: '',
            locality: '',
            province: 'CABA',
            partido: '',
            postalCode: '',
            type: 'Entrega',
            contactType: 'cliente',
            deliveries: '',
            pickups: '',
            deliversTo: '',
            description: '',
            priority: 'Normal',
            schedule: '',
            contactName: '',
            contactPhone: '',
            status: 'pending',
            adminNotes: [],
            latlng: null,
            operationRole: 'mixed',
            operationalLoads: []
          },
      { defaultRole: 'mixed' }
    )
  );

  const [searchTerm, setSearchTerm] = useState(isEditing ? destination.name : '');
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [addressGeocodingError, setAddressGeocodingError] = useState('');
  const [addressValidation, setAddressValidation] = useState(() =>
    formData.latlng
      ? createDestinationGeoState({
          status: 'success',
          text: 'Destino validado y listo para usarse en ruta.',
          validatedAddress: buildDestinationValidatedAddress(formData)
        })
      : createDestinationGeoState()
  );

  useEffect(() => {
    if (searchTerm) {
      setFilteredContacts(
        (contacts || []).filter((c) =>
          c.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredContacts([]);
    }
  }, [searchTerm, contacts]);

  const updateGeoStateFromForm = (nextFormData) => {
    if (nextFormData?.latlng) {
      setAddressValidation(
        createDestinationGeoState({
          status: 'success',
          text: 'Destino validado y listo para usarse en ruta.',
          validatedAddress: buildDestinationValidatedAddress(nextFormData)
        })
      );
      return;
    }

    setAddressValidation((prev) =>
      prev.status === 'error'
        ? prev
        : createDestinationGeoState({
            status: 'idle',
            text: 'La dirección cambió. Validala para guardar coordenadas reales.',
            validatedAddress: ''
          })
    );
  };

  const validateCurrentAddress = async () => {
    if (!formData.address?.trim()) {
      setAddressGeocodingError('Completá la dirección antes de validarla.');
      setAddressValidation(
        createDestinationGeoState({
          status: 'error',
          text: 'Completá la dirección antes de validar el destino.',
          validatedAddress: ''
        })
      );
      return null;
    }

    if (formData.latlng) {
      const alreadyValidated = createDestinationGeoState({
        status: 'success',
        text: 'Destino validado y listo para usarse en ruta.',
        validatedAddress: buildDestinationValidatedAddress(formData)
      });
      setAddressGeocodingError('');
      setAddressValidation(alreadyValidated);
      return formData.latlng;
    }

    setAddressValidation(
      createDestinationGeoState({
        status: 'loading',
        text: 'Validando ubicación en mapa...',
        validatedAddress: ''
      })
    );

    const fullAddress = buildDestinationValidatedAddress(formData);
    const coords = await geocodeAddress(fullAddress, showMessage);

    if (coords) {
      setFormData((prev) => ({ ...prev, latlng: coords }));
      setAddressGeocodingError('');
      setAddressValidation(
        createDestinationGeoState({
          status: 'success',
          text: 'Destino validado y listo para pasar a la ruta.',
          validatedAddress: fullAddress
        })
      );
      return coords;
    }

    setFormData((prev) => ({ ...prev, latlng: null }));
    setAddressGeocodingError(
      'No se pudieron obtener coordenadas. Verificá la dirección.'
    );
    setAddressValidation(
      createDestinationGeoState({
        status: 'error',
        text: 'No pude validar esta dirección. Revisala antes de guardar.',
        validatedAddress: ''
      })
    );
    return null;
  };

  const handleSelectContact = async (contact) => {
    const typeMap = {
      cliente: 'Entrega',
      proveedor: 'Retiro',
      transporte: 'Transporte'
    };

    const selectedAddress = contact.addresses?.[0];
    let selectedLatlng = selectedAddress?.latlng || null;

    if (selectedAddress?.address && !selectedLatlng) {
      const fullAddressParts = [
        selectedAddress.address,
        selectedAddress.apartment,
        selectedAddress.locality,
        selectedAddress.partido,
        selectedAddress.province,
        selectedAddress.postalCode
      ].filter(Boolean);

      const fullAddress = fullAddressParts.join(', ');
      const coords = await geocodeAddress(fullAddress, showMessage);

      if (coords) {
        selectedLatlng = coords;
        setAddressGeocodingError('');
        setAddressValidation(
          createDestinationGeoState({
            status: 'success',
            text: 'Destino existente con ubicación ya validada.',
            validatedAddress: buildDestinationValidatedAddress(selectedAddress)
          })
        );
      } else {
        selectedLatlng = null;
        setAddressGeocodingError(
          'No se pudieron obtener coordenadas. Verifica la dirección.'
        );
        setAddressValidation(
          createDestinationGeoState({
            status: 'error',
            text: 'El contacto existe, pero su dirección no se pudo validar todavía.',
            validatedAddress: ''
          })
        );
      }
    } else {
      setAddressGeocodingError('');
      setAddressValidation(
        selectedLatlng
          ? createDestinationGeoState({
              status: 'success',
              text: 'Destino existente con ubicación ya validada.',
              validatedAddress: buildDestinationValidatedAddress(selectedAddress)
            })
          : createDestinationGeoState({
              status: 'idle',
              text: 'El contacto no tiene coordenadas guardadas. Validá la dirección antes de seguir.',
              validatedAddress: ''
            })
      );
    }

    setFormData((prev) => ({
      ...prev,
      name: contact.name || '',
      address: selectedAddress?.address || '',
      apartment: selectedAddress?.apartment || '',
      locality: selectedAddress?.locality || '',
      province: selectedAddress?.province || 'CABA',
      partido: selectedAddress?.partido || '',
      postalCode: selectedAddress?.postalCode || '',
      contactName: contact.contacts?.[0]?.name || '',
      contactPhone: contact.contacts?.[0]?.phone || '',
      type: typeMap[contact.type] || 'Entrega',
      contactType: contact.type || 'cliente',
      latlng: selectedLatlng
    }));

    setSearchTerm('');
    setFilteredContacts([]);
  };

  const buildAddressPayload = (latlng) => ({
    address: formData.address,
    apartment: formData.apartment,
    locality: formData.locality,
    province: formData.province,
    partido: formData.partido,
    postalCode: formData.postalCode,
    latlng: latlng || null
  });

  const buildContactPersonPayload = () => {
    const name = formData.contactName?.trim();
    const phone = formData.contactPhone?.trim();

    if (!name && !phone) return [];
    return [{ name: name || '', phone: phone || '' }];
  };

  const handleSave = async () => {
    if (!formData.name?.trim() || !formData.address?.trim()) {
      showMessage('Nombre y dirección son obligatorios', 'error');
      return;
    }

    let finalLatlng = formData.latlng || (await validateCurrentAddress());
    if (!finalLatlng) {
      showMessage('No se pudo guardar el destino. Revisá la dirección.', 'error');
      return;
    }

    setAddressGeocodingError('');

    try {
      const contactsRef = collection(db, contactsCollectionPath);
      const q = query(contactsRef, where('name', '==', formData.name.trim()));
      const querySnapshot = await getDocs(q);

      const newAddressPayload = buildAddressPayload(finalLatlng);
      const newContactPersons = buildContactPersonPayload();

      if (!querySnapshot.empty) {
        const existingContactDoc = querySnapshot.docs[0];
        const existingContactData = existingContactDoc.data();

        const existingAddresses = Array.isArray(existingContactData.addresses)
          ? [...existingContactData.addresses]
          : [];

        const existingAddressIndex = existingAddresses.findIndex(
          (addr) =>
            (addr.address || '').trim() === formData.address.trim() &&
            (addr.apartment || '').trim() === (formData.apartment || '').trim()
        );

        if (existingAddressIndex === -1) {
          existingAddresses.push(newAddressPayload);
        } else {
          existingAddresses[existingAddressIndex] = {
            ...existingAddresses[existingAddressIndex],
            ...newAddressPayload
          };
        }

        const existingContacts = Array.isArray(existingContactData.contacts)
          ? [...existingContactData.contacts]
          : [];

        const shouldAddContactPerson =
          newContactPersons.length > 0 &&
          !existingContacts.some(
            (c) =>
              (c.name || '').trim() === (newContactPersons[0].name || '').trim() &&
              (c.phone || '').trim() === (newContactPersons[0].phone || '').trim()
          );

        await updateDoc(doc(db, contactsCollectionPath, existingContactDoc.id), {
          addresses: existingAddresses,
          contacts: shouldAddContactPerson
            ? [...existingContacts, ...newContactPersons]
            : existingContacts,
          type: existingContactData.type || formData.contactType || 'cliente',
          companyId: existingContactData.companyId || resolvedCompanyId
        });
      } else {
        await addDoc(contactsRef, {
          name: formData.name.trim(),
          type: formData.contactType || 'cliente',
          addresses: [newAddressPayload],
          contacts: newContactPersons,
          companyId: resolvedCompanyId
        });
      }
    } catch (error) {
      console.error('Error al sincronizar contacto desde DestinationFormModal:', error);
      showMessage('El destino se guardará, pero hubo un problema al actualizar el contacto.', 'warning');
    }

    const normalizedDestination = normalizeDestinationOperationalFields(
      {
        ...formData,
        name: formData.name.trim(),
        latlng: finalLatlng,
        operationalLoads: (formData.operationalLoads || []).map((load) =>
          normalizeOperationalLoad(load, {
            originStopId: load?.originStopId || formData.id,
            targetStopId: load?.targetStopId || formData.deliversTo || ''
          })
        )
      },
      { defaultRole: 'mixed' }
    );

    onSave(normalizedDestination);
  };

  const handleOperationalLoadChange = (loadId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      operationalLoads: (prev.operationalLoads || []).map((load) => {
        if (load.id !== loadId) return load;

        const patch = { [field]: value };
        if (field === 'quantity') {
          patch.quantity = Math.max(1, Number(value) || 1);
        }

        return normalizeOperationalLoad(
          {
            ...load,
            ...patch
          },
          {
            originStopId: load.originStopId || prev.id,
            targetStopId: load.targetStopId || prev.deliversTo || ''
          }
        );
      })
    }));
  };

  const handleAddOperationalLoad = () => {
    setFormData((prev) => ({
      ...prev,
      operationalLoads: [
        ...(prev.operationalLoads || []),
        createEmptyOperationalLoad(prev.id)
      ]
    }));
  };

  const handleRemoveOperationalLoad = (loadId) => {
    setFormData((prev) => ({
      ...prev,
      operationalLoads: (prev.operationalLoads || []).filter((load) => load.id !== loadId)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[80]">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">
            {isEditing ? 'Editar Destino' : 'Añadir Destino a la Ruta'}
          </h3>

          <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-200">
            <Icon path="M6 18L18 6M6 6l12 12" className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto pr-2 space-y-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar contacto para autocompletar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border rounded-md"
            />

            {filteredContacts.length > 0 && (
              <div className="absolute z-10 w-full bg-white border rounded-md mt-1 max-h-48 overflow-y-auto shadow-lg">
                {filteredContacts.map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => handleSelectContact(contact)}
                    className="p-2 hover:bg-blue-100 cursor-pointer"
                  >
                    <p className="font-semibold">{contact.name}</p>
                    <p className="text-sm text-slate-500">
                      {contact.addresses?.[0]?.address}{' '}
                      {contact.addresses?.[0]?.apartment &&
                        `(${contact.addresses?.[0]?.apartment})`}
                      <br />
                      {[
                        contact.addresses?.[0]?.locality,
                        contact.addresses?.[0]?.partido,
                        contact.addresses?.[0]?.province,
                        contact.addresses?.[0]?.postalCode
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <h4 className="font-semibold border-t pt-4 mt-4">O rellenar manualmente:</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Nombre Cliente/Proveedor"
              value={formData.name}
              onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
              className="w-full p-2 border rounded-md md:col-span-2"
            />

            <input
              type="text"
              placeholder="Dirección (Calle, N°, Ciudad, Provincia)"
              value={formData.address}
              onChange={(e) => {
                setFormData((d) => {
                  const next = { ...d, address: e.target.value, latlng: null };
                  updateGeoStateFromForm(next);
                  return next;
                });
                setAddressGeocodingError('');
              }}
              onBlur={validateCurrentAddress}
              className="w-full p-2 border rounded-md md:col-span-2"
            />

            <input
              type="text"
              placeholder="Casa/Depto/Piso"
              value={formData.apartment}
              onChange={(e) =>
                setFormData((d) => {
                  const next = { ...d, apartment: e.target.value, latlng: null };
                  updateGeoStateFromForm(next);
                  return next;
                })
              }
              className="w-full p-2 border rounded-md"
            />

            <select
              value={formData.contactType}
              onChange={(e) =>
                setFormData((d) => ({ ...d, contactType: e.target.value }))
              }
              className="w-full p-2 border rounded-md bg-white capitalize"
            >
              <option value="cliente">Cliente</option>
              <option value="proveedor">Proveedor</option>
              <option value="transporte">Transporte</option>
            </select>

            <select
              value={formData.province}
              onChange={(e) =>
                setFormData((d) => {
                  const next = { ...d, province: e.target.value, latlng: null };
                  updateGeoStateFromForm(next);
                  return next;
                })
              }
              className="w-full p-2 border rounded-md bg-white"
            >
              {ARGENTINA_PROVINCES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            {formData.province === 'Buenos Aires' && (
              <input
                type="text"
                placeholder="Partido"
                value={formData.partido}
                onChange={(e) =>
                  setFormData((d) => {
                    const next = { ...d, partido: e.target.value, latlng: null };
                    updateGeoStateFromForm(next);
                    return next;
                  })
                }
                className="w-full p-2 border rounded-md"
              />
            )}

            <input
              type="text"
              value={formData.locality}
              onChange={(e) =>
                setFormData((d) => {
                  const next = { ...d, locality: e.target.value, latlng: null };
                  updateGeoStateFromForm(next);
                  return next;
                })
              }
              placeholder="Localidad"
              className="w-full p-2 border rounded-md"
            />

            <input
              type="text"
              value={formData.postalCode}
              onChange={(e) =>
                setFormData((d) => {
                  const next = { ...d, postalCode: e.target.value, latlng: null };
                  updateGeoStateFromForm(next);
                  return next;
                })
              }
              placeholder="Código Postal"
              className="w-full p-2 border rounded-md"
            />

            <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Validación de ubicación
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{addressValidation.text}</p>
                  {addressValidation.validatedAddress ? (
                    <p className="mt-1 text-xs text-emerald-700">
                      Dirección aprobada: {addressValidation.validatedAddress}
                    </p>
                  ) : null}
                  {addressGeocodingError ? (
                    <p className="mt-2 flex items-center gap-1 text-xs text-rose-600">
                      <Icon
                        path="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                        className="w-4 h-4"
                      />
                      {addressGeocodingError}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={validateCurrentAddress}
                  className="inline-flex items-center gap-2 rounded-2xl border border-cyan-200 bg-white px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-50"
                >
                  <Icon path="M12 2.25c-4.97 0-9 3.806-9 8.5 0 6.396 8.196 10.517 8.545 10.69a1 1 0 00.91 0C12.804 21.267 21 17.146 21 10.75c0-4.694-4.03-8.5-9-8.5z" className="w-4 h-4" />
                  Validar ubicación en mapa
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span
                  className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
                    formData.latlng
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-white text-slate-500'
                  }`}
                >
                  {formData.latlng
                    ? `Lat ${Number(formData.latlng.lat || 0).toFixed(5)} · Lng ${Number(
                        formData.latlng.lng || 0
                      ).toFixed(5)}`
                    : 'Todavía sin coordenadas validadas'}
                </span>
              </div>
            </div>

            <input
              type="text"
              placeholder="Nombre de Contacto"
              value={formData.contactName}
              onChange={(e) =>
                setFormData((d) => ({ ...d, contactName: e.target.value }))
              }
              className="w-full p-2 border rounded-md"
            />

            <input
              type="text"
              placeholder="Teléfono de Contacto"
              value={formData.contactPhone}
              onChange={(e) =>
                setFormData((d) => ({ ...d, contactPhone: e.target.value }))
              }
              className="w-full p-2 border rounded-md"
            />

            <textarea
              placeholder="Descripción del Pedido (ej: 3 cajas, fragil, N° remito)"
              value={formData.description}
              onChange={(e) =>
                setFormData((d) => ({ ...d, description: e.target.value }))
              }
              className="w-full p-2 border rounded-md"
            />

            <div className="md:col-span-2 rounded-2xl border border-cyan-200 bg-cyan-50/50 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-700">
                    Carga operativa
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    La descripción libre sigue existiendo. Acá le damos entidad a lo que se mueve.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleAddOperationalLoad}
                  className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-cyan-700"
                >
                  <Icon path="M12 9v6m3-3H9" className="w-4 h-4" />
                  Añadir carga
                </button>
              </div>

              <div className="mb-3">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Rol operativo de la parada
                </label>
                <select
                  value={formData.operationRole}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, operationRole: e.target.value }))
                  }
                  className="w-full p-2 border rounded-md bg-white"
                >
                  {DESTINATION_OPERATION_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {getDestinationOperationRoleLabel(role)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-3">
                {(formData.operationalLoads || []).length === 0 && (
                  <div className="rounded-xl border border-dashed border-cyan-200 bg-white/80 px-4 py-4 text-sm text-slate-500">
                    Todavía no hay cargas estructuradas. Podés seguir usando solo la descripción libre o sumar una carga ahora.
                  </div>
                )}

                {(formData.operationalLoads || []).map((load, index) => (
                  <div key={load.id} className="rounded-xl border border-cyan-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800">
                        Carga {index + 1}
                      </p>

                      <button
                        type="button"
                        onClick={() => handleRemoveOperationalLoad(load.id)}
                        className="text-xs font-semibold uppercase tracking-[0.12em] text-rose-600"
                      >
                        Quitar
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <input
                        type="text"
                        placeholder="Título de la carga"
                        value={load.title}
                        onChange={(e) => handleOperationalLoadChange(load.id, 'title', e.target.value)}
                        className="w-full p-2 border rounded-md"
                      />

                      <input
                        type="text"
                        placeholder="Categoría"
                        value={load.category}
                        onChange={(e) => handleOperationalLoadChange(load.id, 'category', e.target.value)}
                        className="w-full p-2 border rounded-md"
                      />

                      <input
                        type="number"
                        min="1"
                        placeholder="Cantidad"
                        value={load.quantity}
                        onChange={(e) => handleOperationalLoadChange(load.id, 'quantity', e.target.value)}
                        className="w-full p-2 border rounded-md"
                      />

                      <select
                        value={load.unit}
                        onChange={(e) => handleOperationalLoadChange(load.id, 'unit', e.target.value)}
                        className="w-full p-2 border rounded-md bg-white"
                      >
                        {OPERATIONAL_LOAD_UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>

                      <select
                        value={load.operationType}
                        onChange={(e) => handleOperationalLoadChange(load.id, 'operationType', e.target.value)}
                        className="w-full p-2 border rounded-md bg-white"
                      >
                        {OPERATIONAL_LOAD_OPERATION_TYPES.map((operationType) => (
                          <option key={operationType} value={operationType}>
                            {getDestinationOperationRoleLabel(operationType)}
                          </option>
                        ))}
                      </select>

                      <input
                        type="text"
                        placeholder="Código de referencia"
                        value={load.referenceCode}
                        onChange={(e) => handleOperationalLoadChange(load.id, 'referenceCode', e.target.value)}
                        className="w-full p-2 border rounded-md"
                      />

                      <textarea
                        placeholder="Descripción de la carga"
                        value={load.description}
                        onChange={(e) => handleOperationalLoadChange(load.id, 'description', e.target.value)}
                        className="w-full p-2 border rounded-md md:col-span-2"
                      />

                      <div className="md:col-span-2 rounded-xl bg-slate-50 px-3 py-3 text-xs text-slate-600">
                        <p>
                          <span className="font-semibold text-slate-800">Origen:</span> esta parada
                        </p>
                        <p className="mt-1">
                          <span className="font-semibold text-slate-800">Destino final:</span>{' '}
                          {load.targetStopId || formData.deliversTo || 'se define más adelante'}
                        </p>
                      </div>

                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={load.requiresEvidenceOnPickup}
                          onChange={(e) =>
                            handleOperationalLoadChange(load.id, 'requiresEvidenceOnPickup', e.target.checked)
                          }
                        />
                        Evidencia en retiro
                      </label>

                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={load.requiresEvidenceOnDelivery}
                          onChange={(e) =>
                            handleOperationalLoadChange(load.id, 'requiresEvidenceOnDelivery', e.target.checked)
                          }
                        />
                        Evidencia en entrega
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <select
              value={formData.type}
              onChange={(e) => setFormData((d) => ({ ...d, type: e.target.value }))}
              className="w-full p-2 border rounded-md bg-white"
            >
              <option>Entrega</option>
              <option>Retiro</option>
              <option>Entrega y Retiro</option>
              <option>Transporte</option>
            </select>

            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData((d) => ({ ...d, priority: e.target.value }))
              }
              className="w-full p-2 border rounded-md bg-white"
            >
              <option value="Normal">Normal</option>
              <option value="Importante">Importante</option>
              <option value="Urgente">Urgente</option>
            </select>

            <input
              type="text"
              placeholder="Horario (ej: 14:00 a 18:00)"
              value={formData.schedule}
              onChange={(e) =>
                setFormData((d) => ({ ...d, schedule: e.target.value }))
              }
              className="w-full p-2 border rounded-md"
            />

            {formData.type.includes('Entrega') && (
              <input
                type="number"
                placeholder="N° Pedidos a Entregar"
                value={formData.deliveries}
                onChange={(e) =>
                  setFormData((d) => ({ ...d, deliveries: e.target.value }))
                }
                className="w-full p-2 border rounded-md"
              />
            )}

            {formData.type.includes('Retiro') && (
              <>
                <input
                  type="number"
                  placeholder="N° Pedidos a Retirar"
                  value={formData.pickups}
                  onChange={(e) =>
                    setFormData((d) => ({ ...d, pickups: e.target.value }))
                  }
                  className="w-full p-2 border rounded-md"
                />

                <select
                  value={formData.deliversTo}
                  onChange={(e) =>
                    setFormData((d) => ({ ...d, deliversTo: e.target.value }))
                  }
                  className="w-full p-2 border rounded-md bg-white md:col-span-2"
                >
                  <option value="">Llevar retiro a...</option>
                  <option value="oficina">Oficina</option>
                  {route.destinations
                    .filter((d) => d.type.includes('Entrega') && d.id !== formData.id)
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        Cliente: {d.name}
                      </option>
                    ))}
                </select>
              </>
            )}
          </div>
        </div>

        <div className="mt-auto pt-4 border-t">
          <button
            onClick={handleSave}
            className="w-full bg-blue-600 text-white font-bold py-2 rounded-md hover:bg-blue-700 mt-2"
          >
            {isEditing ? 'Guardar Cambios' : 'Añadir a la Ruta'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DestinationFormModal;
